/**
 * Auth store —— admin 端登录态
 *
 * 持久化策略：access + refresh 写 localStorage，刷新页面恢复。
 * userId 从 access token payload 解出（懒解，避免每次反查后端）。
 *
 * 不在此处做"401 自动 refresh"——那是 axios client 的责任。Day 17 简化版：
 * 401 直接 clearTokens + 跳 /login（带 redirect query）。
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { MeInfo } from '@/api/admin-auth'

const STORAGE_KEY = 'yindu-admin-auth-v1'

interface PersistedAuth {
  accessToken: string | null
  refreshToken: string | null
}

interface JwtPayloadShape {
  sub?: string
  aud?: string
  exp?: number
}

function loadPersisted(): PersistedAuth {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return { accessToken: null, refreshToken: null }
    const parsed = JSON.parse(raw) as PersistedAuth
    return {
      accessToken: typeof parsed.accessToken === 'string' ? parsed.accessToken : null,
      refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : null,
    }
  } catch {
    return { accessToken: null, refreshToken: null }
  }
}

function persist(state: PersistedAuth): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // localStorage 不可用（隐私模式/磁盘满）—— 静默失败
  }
}

function decodeJwtPayload(token: string | null): JwtPayloadShape | null {
  if (token === null) return null
  const parts = token.split('.')
  const payloadPart = parts[1]
  if (payloadPart === undefined) return null
  try {
    // base64url → base64
    const padded = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(padded)
    return JSON.parse(decoded) as JwtPayloadShape
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const persisted = loadPersisted()
  const accessToken = ref<string | null>(persisted.accessToken)
  const refreshToken = ref<string | null>(persisted.refreshToken)
  // me 不持久化：内存里存，刷新后由 DefaultLayout 通过 fetchMe 重新加载
  const me = ref<MeInfo | null>(null)

  const userId = computed<string | null>(() => decodeJwtPayload(accessToken.value)?.sub ?? null)
  const audience = computed<string | null>(() => decodeJwtPayload(accessToken.value)?.aud ?? null)
  const isAuthenticated = computed<boolean>(() => accessToken.value !== null)

  function setTokens(access: string, refresh: string): void {
    accessToken.value = access
    refreshToken.value = refresh
    persist({ accessToken: access, refreshToken: refresh })
  }

  function setMe(info: MeInfo): void {
    me.value = info
  }

  function clearTokens(): void {
    accessToken.value = null
    refreshToken.value = null
    me.value = null
    persist({ accessToken: null, refreshToken: null })
  }

  return {
    accessToken,
    refreshToken,
    me,
    userId,
    audience,
    isAuthenticated,
    setTokens,
    setMe,
    clearTokens,
  }
})
